import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { SelectButtonModule } from 'primeng/selectbutton';
import { PaginatorModule } from 'primeng/paginator';
import { MenuModule } from 'primeng/menu';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { CardModule } from 'primeng/card';
import { BadgeModule } from 'primeng/badge';
import { AvatarModule } from 'primeng/avatar';
import { TooltipModule } from 'primeng/tooltip';
import { DrawerModule } from 'primeng/drawer';
import { MessageService, ConfirmationService, MenuItem } from 'primeng/api';
import { UserService, User, PagedUsersRequest, PagedUsersResponse, PendingApproval } from '../../../core/services/user.service';
import { AuthService } from '../../../core/services/auth';
import { ProductService, Product } from '../../../core/services/product.service';
import { TerritoryService, GroupedTerritories } from '../../../core/services/territory.service';
import { RoleService, Role, RoleWithPermissions } from '../../../core/services/role.service';
import { SubscriptionContextService } from '../../../core/services/subscription-context.service';
import { SubscriberService } from '../../../core/services/subscriber.service';
import { environment } from '../../../../environments/environment';
import { IconFieldModule } from 'primeng/iconfield';
import { Menu } from 'primeng/menu';
import { TagModule } from 'primeng/tag';
import { InputIcon } from 'primeng/inputicon';
import { AccordionModule } from 'primeng/accordion';
import { Router, ActivatedRoute } from '@angular/router';
import { EncryptionService } from '../../../core/services/encryption';
import { SETTINGS } from '../../../core/config/common.settings';
import { PermissionService } from '../../../core/services/permission.service';
import { Subscription } from 'rxjs';
import { FileIconService, FileIconInfo } from '../../../core/services/file-icon.service';
@Component({
  selector: 'app-view-user',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    SelectButtonModule,
    PaginatorModule,
    MenuModule,
    ConfirmDialogModule,
    ToastModule,
    CardModule,
    BadgeModule,
    AvatarModule,
    TooltipModule,
    DrawerModule,
    TagModule,
    IconFieldModule,
    InputIcon,
    AccordionModule
  ],
  providers: [MessageService, ConfirmationService],
  templateUrl: './view-user.html',
  styleUrl: './view-user.scss'
})
export class ViewUser implements OnInit, OnDestroy {
  @ViewChild('menu') menu!: Menu;
  
  users: User[] = [];
  pendingApprovals: PendingApproval[] = [];
  totalUsers = 0;
  loading = false;
  currentPage = 1;
  pageSize = 10;
  pageSizes = [10, 20, 50, 100];
  
  // Math property for template
  Math = Math;
  
  // Filters
  searchText = '';
  selectedStatus: any = null;
  selectedRole: any = null;
  private _selectedArchived: any = false; // Default to Active (false)
  
  get selectedArchived(): any {
    return this._selectedArchived;
  }
  
  set selectedArchived(value: any) {
    // Ensure the value is always a boolean, never null/undefined
    if (value !== true && value !== false) {
      this._selectedArchived = false; // Default to Active
    } else {
      this._selectedArchived = value;
    }
  }
  
  // Sorting
  sortField = 'createdAt';
  sortOrder: 1 | -1 = -1; // -1 for descending (newest first), 1 for ascending (oldest first)

  // Filter options
  statusOptions = [
    { label: 'All Status', value: null },
    { label: 'Invited', value: 'INVITED' },
    { label: 'Active', value: 'APPROVED' },
    { label: 'Pending', value: 'PENDING' },
    { label: 'Denied', value: 'DENIED' },
    { label: 'Locked', value: 'LOCKED' },
    { label: 'Deactivated', value: 'DEACTIVATED' }
  ];
  
  roleOptions: { label: string; value: string | null }[] = [
    { label: 'All Roles', value: null }
  ];
  
  archivedOptions = [ 
    { label: 'Active', value: false },
    { label: 'Archived', value: true }
  ];

  getArchivedOptions() {
    return this.archivedOptions.map(option => ({
      ...option,
      disabled: option.value === this.selectedArchived
    }));
  }

  // User actions menu
  actionMenuItems: MenuItem[] = [];
  selectedUser: User | null = null;

  // Drawer for user review
  isDrawerVisible = false;
  selectedUserForReview: User | null = null;
  userReviewLoading = false;

  // Additional loading states
  loadingRoles = false;
  approvingUser = false;
  denyingUser = false;

  // Current user context for role hierarchy checks
  currentUser: any = null;

  // Selected subscription name
  selectedSubscriptionName: string | null = null;
  
  // Subscription for cleanup
  private subscriptionContextSubscription?: Subscription;

  constructor(
    private userService: UserService,
    private authService: AuthService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private route: ActivatedRoute,
    private productService: ProductService,
    private territoryService: TerritoryService,
    private roleService: RoleService,
    private subscriptionContextService: SubscriptionContextService,
    private subscriberService: SubscriberService,
    private encryption: EncryptionService,
    private permissionService: PermissionService,
    private fileIconService: FileIconService
  ) {}

  ngOnInit() {
    // Load current user context first
    this.currentUser = this.authService.getLoggedInUser();
    console.log('ViewUser - Current User:', this.currentUser);
    
    // Load selected subscription name
    this.loadSelectedSubscriptionName();
    
    // Subscribe to subscription changes
    this.subscriptionContextSubscription = this.subscriptionContextService.selectedSubscriber$.subscribe(subscriber => {
      if (subscriber.companyName) {
        this.selectedSubscriptionName = subscriber.companyName;
      } else if (subscriber.subscriberId) {
        // Fetch companyName if not available in context
        this.fetchSubscriptionName(subscriber.subscriberId);
      } else {
        this.selectedSubscriptionName = null;
      }
    });
    
    // Check for query parameters to apply filters (using snapshot for initial load)
    const queryParams = this.route.snapshot.queryParams;
    
    // Check for userId parameter to auto-open user review drawer
    if (queryParams['userId']) {
      const userId = queryParams['userId'];
      // Use setTimeout to ensure component is fully initialized
      setTimeout(() => {
        this.openUserReview(userId);
        // Remove the query parameter from URL to clean it up
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { userId: null },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }, 200);
    }
    
    if (queryParams['filterActiveFSRs'] === 'true') {
      // Apply Active FSRs filter
      this.selectedRole = 'FSR';
      this.selectedStatus = 'APPROVED';
      this.selectedArchived = false;
    } else {
      // Apply individual filter parameters if present
      if (queryParams['roleId']) {
        this.selectedRole = queryParams['roleId'];
      }
      if (queryParams['status']) {
        this.selectedStatus = queryParams['status'];
      }
      if (queryParams['archived'] !== undefined) {
        this.selectedArchived = queryParams['archived'] === 'true';
      }
    }
    
    this.loadRoles();
    this.loadUsers();
    this.loadPendingApprovals();
    this.setupActionMenu();
  }

  loadSelectedSubscriptionName() {
    const selectedSubscriber = this.subscriptionContextService.getSelectedSubscriber();
    
    // First try to get from context
    if (selectedSubscriber.companyName) {
      this.selectedSubscriptionName = selectedSubscriber.companyName;
      return;
    }
    
    // Check if it matches current user's subscriber
    if (selectedSubscriber.subscriberId && this.currentUser?.subscriberId) {
      const currentUserSubscriberId = typeof this.currentUser.subscriberId === 'object' 
        ? this.currentUser.subscriberId._id 
        : this.currentUser.subscriberId;
      
      if (selectedSubscriber.subscriberId === currentUserSubscriberId) {
        const companyName = typeof this.currentUser.subscriberId === 'object' 
          ? this.currentUser.subscriberId.companyName 
          : undefined;
        if (companyName) {
          this.selectedSubscriptionName = companyName;
          return;
        }
      }
    }
    
    // Fetch from subscriber service if subscriberId is available
    if (selectedSubscriber.subscriberId) {
      this.fetchSubscriptionName(selectedSubscriber.subscriberId);
    } else {
      this.selectedSubscriptionName = null;
    }
  }

  fetchSubscriptionName(subscriberId: string) {
    this.subscriberService.findSubscriber(subscriberId).then((response: any) => {
      // Handle different response structures
      const subscriber = response?.subscriber || response;
      if (subscriber?.companyName) {
        this.selectedSubscriptionName = subscriber.companyName;
        // Update the subscription context with the companyName for future use
        this.subscriptionContextService.setSelectedSubscriber(subscriberId, subscriber.companyName);
      }
    }).catch((error: any) => {
      console.error('Error fetching subscriber name:', error);
      // Keep null if fetch fails
      this.selectedSubscriptionName = null;
    });
  }

  ngOnDestroy() {
    if (this.subscriptionContextSubscription) {
      this.subscriptionContextSubscription.unsubscribe();
    }
  }

  setupActionMenu() {
    this.actionMenuItems = [
      {
        label: 'Edit',
        icon: 'pi pi-pencil',
        command: () => this.editUser()
      },
      {
        label: 'Send New Password',
        icon: 'pi pi-envelope',
        command: () => this.generateAndSendNewPassword()
      }
    ];
  }

  updateActionMenuItems(user: User) {
    this.actionMenuItems = [];

    // Edit User - requires user.update permission and user status must not be INVITED
    if (this.hasUpdatePermission() && user.status !== 'INVITED') {
      this.actionMenuItems.push({
        label: 'Edit',
        icon: 'pi pi-pencil',
        command: () => this.editUser()
      });
    }

    // Send New Password - requires user.password.generate permission
    if (this.hasPasswordGeneratePermission()) {
      this.actionMenuItems.push({
        label: 'Send New Password',
        icon: 'pi pi-envelope',
        command: () => this.generateAndSendNewPassword()
      });
    }

    // Add conditional items based on user status and permissions
    if ((user.status === 'DEACTIVATED' || user.status === 'DENIED' || user.status === 'LOCKED') && this.hasActivatePermission()) {
      this.actionMenuItems.push({
        label: 'Activate User',
        icon: 'pi pi-check',
        command: () => this.activateUser()
      });
    }

    if (user.status === 'APPROVED' && this.hasDeactivatePermission()) {
      this.actionMenuItems.push({
        label: 'Deactivate User',
        icon: 'pi pi-ban',
        command: () => this.deactivateUser()
      });
    }

    if (!user.isArchived && this.hasArchivePermission()) {
      this.actionMenuItems.push({
        label: 'Archive User',
        icon: 'pi pi-trash',
        command: () => this.archiveUser()
      });
    }

    if (user.isArchived && this.hasRestorePermission()) {
      this.actionMenuItems.push({
        label: 'Unarchive User',
        icon: 'pi pi-undo',
        command: () => this.unarchiveUser()
      });
    }

    // Lock/Unlock User - requires user.lock/user.unlock permissions
    if (user.status !== 'LOCKED' && this.hasLockPermission()) {
      this.actionMenuItems.push({
        label: 'Lock User',
        icon: 'pi pi-lock',
        command: () => this.lockUser(user._id)
      });
    }

    if (user.status === 'LOCKED' && this.hasUnlockPermission()) {
      this.actionMenuItems.push({
        label: 'Unlock User',
        icon: 'pi pi-unlock',
        command: () => this.unlockUser(user._id)
      });
    }
  }

  // Role hierarchy methods
  private getRoleHierarchy() {
    return {
      'SUPER_DEV_ADMIN': 6, // Highest level
      'CSM': 5,
      'SM': 4, 
      'SA': 3,
      'A': 2,
      'FSR': 1  // Lowest level
    };
  }

  private getCurrentUserRole(): string {
    return this.currentUser?.roleId?.identification || this.currentUser?.role?.identification || '';
  }

  private getCurrentUserLevel(): number {
    const currentRole = this.getCurrentUserRole();
    const hierarchy = this.getRoleHierarchy();
    return hierarchy[currentRole as keyof typeof hierarchy] || 0;
  }

  private getUserLevel(user: User): number {
    const userRole = user.roleId?.identification || '';
    const hierarchy = this.getRoleHierarchy();
    return hierarchy[userRole as keyof typeof hierarchy] || 0;
  }

  // Check if current user can review the given user (must be higher in hierarchy)
  canReviewUser(user: User): boolean {
    const currentUserLevel = this.getCurrentUserLevel();
    const targetUserLevel = this.getUserLevel(user);
    
    // FSR cannot review anyone
    if (this.getCurrentUserRole() === 'FSR') {
      return false;
    }
    
    // Can only review users that are lower in hierarchy
    return currentUserLevel > targetUserLevel;
  }

  // Check if current user is FSR
  isFSR(): boolean {
    return this.getCurrentUserRole() === 'FSR';
  }

  // Check if current user has delete permission using centralized service
  hasDeletePermission(): boolean {
    return this.permissionService.hasPermission('user.delete');
  }

  // Check if current user has archive permission using centralized service
  hasArchivePermission(): boolean {
    return this.permissionService.hasPermission('user.archive');
  }

  // Check if current user has restore permission using centralized service
  hasRestorePermission(): boolean {
    return this.permissionService.hasPermission('user.restore');
  }

  // Check if current user has activate permission using centralized service
  hasActivatePermission(): boolean {
    return this.permissionService.hasPermission('user.activate');
  }

  // Check if current user has deactivate permission using centralized service
  hasDeactivatePermission(): boolean {
    return this.permissionService.hasPermission('user.deactivate');
  }

  // Check if current user has approve permission using centralized service
  hasApprovePermission(): boolean {
    return this.permissionService.hasPermission('user.approve');
  }

  // Check if current user has deny permission using centralized service
  hasDenyPermission(): boolean {
    return this.permissionService.hasPermission('user.deny');
  }

  // Check if current user has lock permission using centralized service
  hasLockPermission(): boolean {
    return this.permissionService.hasPermission('user.lock');
  }

  // Check if current user has unlock permission using centralized service
  hasUnlockPermission(): boolean {
    return this.permissionService.hasPermission('user.unlock');
  }

  // Check if current user has password generate permission using centralized service
  hasPasswordGeneratePermission(): boolean {
    return this.permissionService.hasPermission('user.password.generate');
  }

  // Check if current user has update permission using centralized service
  hasUpdatePermission(): boolean {
    return this.permissionService.hasPermission('user.update');
  }

  // Check if current user has create permission using centralized service
  hasCreateUserPermission(): boolean {
    return this.permissionService.hasPermission('user.create');
  }

  // Check if current user has pending view permission using centralized service
  hasPendingViewPermission(): boolean {
    return this.permissionService.hasPermission('user.pending.view');
  }

  loadRoles() {
    this.loadingRoles = true;
    // Use the same logic as the available roles endpoint
    // If no selectedSubscriberId from interceptor, return isBaseRole:true CSM,SM + CUSTOM without subscriberId
    // Exclude A, FSR, SA
    this.roleService.getAvailableRoles().subscribe({
      next: (roles: RoleWithPermissions[]) => {
        console.log('Loaded roles:', roles);
        
        // Reset role options with "All Roles"
        this.roleOptions = [{ label: 'All Roles', value: null }];
        
        // Check if a subscription is selected
        const selectedSubscriber = this.subscriptionContextService.getSelectedSubscriber();
        const hasSelectedSubscription = selectedSubscriber && selectedSubscriber.subscriberId;
        
        // Add roles to options
        roles.forEach((role: RoleWithPermissions) => {
          // Hide SA role if no subscription is selected
          if (role.identification === 'SA' && !hasSelectedSubscription) {
            return; // Skip adding SA role to options
          }
          
          this.roleOptions.push({
            label: `${role.name} (${role.identification})`,
            value: role._id // Use role _id instead of identification for precise filtering
          });
        });
        
        // If selectedRole is set to an identification string (from query params or filterActiveFSRs),
        // find the corresponding role and update selectedRole to use the role _id
        if (this.selectedRole && typeof this.selectedRole === 'string' && !/^[0-9a-fA-F]{24}$/.test(this.selectedRole)) {
          // It's an identification string, not an ObjectId
          const matchingRole = roles.find(role => role.identification === this.selectedRole);
          if (matchingRole) {
            this.selectedRole = matchingRole._id;
          } else {
            // Role not found, reset to null
            this.selectedRole = null;
          }
        }
        
        console.log('Role options:', this.roleOptions);
        this.loadingRoles = false;
      },
      error: (error: any) => {
        console.error('Error loading roles:', error);
        // Fallback to basic options if the service fails
        this.roleOptions = [
          { label: 'All Roles', value: null },
          { label: 'CSM', value: 'CSM' },
          { label: 'SM', value: 'SM' }
        ];
        this.loadingRoles = false;
      }
    });
  }

  loadUsers() {
    this.loading = true;
    
    // Prepare filters - only include non-null/undefined values
    const filters: any = {};
    
    if (this.searchText && this.searchText.trim()) {
      filters.searchText = this.searchText.trim();
    }
    
    if (this.selectedStatus !== null && this.selectedStatus !== undefined) {
      filters.status = this.selectedStatus;
    }
    
    if (this.selectedRole !== null && this.selectedRole !== undefined) {
      filters.roleId = this.selectedRole;
    }
    
    // Always include archived filter since it's always set to a boolean value
    filters.archived = this.selectedArchived;
    
    const request: PagedUsersRequest = {
      pageIndex: this.currentPage,
      pageSize: this.pageSize,
      sortField: this.sortField,
      sortOrder: this.sortOrder,
      filters: Object.keys(filters).length > 0 ? filters : undefined
    };

    this.userService.getPagedUsers(request).subscribe({
      next: (response: PagedUsersResponse) => {
        this.users = response.data || [];
        this.totalUsers = response.metadata?.[0]?.total || 0;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading users:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load users'
        });
        this.loading = false;
      }
    });
  }

  applyFilters() {
    // Ensure selectedArchived is always a boolean value (never null/undefined)
    // If it's not a valid boolean, default to Active (false)
    if (this.selectedArchived !== true && this.selectedArchived !== false) {
      this.selectedArchived = false; // Default to Active
    }
    
    this.currentPage = 1;
    this.loadUsers();
  }

  onArchivedChange(event: any) {
    // Since the current option is disabled, the event.value will be the other option
    // Just use the event value directly since only the non-disabled option can be clicked
    this.selectedArchived = event.value;
    this.applyFilters();
  }

  resetFilters() {
    this.searchText = '';
    this.selectedStatus = null;
    this.selectedRole = null;
    this.selectedArchived = false; // Always default to Active (setter will handle validation)
    this.currentPage = 1;
    this.resetSorting();
    this.loadUsers();
  }

  resetSorting() {
    this.sortField = 'createdAt';
    this.sortOrder = -1;
  }

  toggleSort() {
    this.sortOrder = this.sortOrder === -1 ? 1 : -1;
    this.loadUsers();
  }

  loadPendingApprovals() {
    this.userService.getPendingApprovals().subscribe({
      next: (approvals) => {
        this.pendingApprovals = approvals || [];
      },
      error: (error) => {
        console.error('Error loading pending approvals:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load pending approvals'
        });
      }
    });
  }

  filterPendingUsers() {
    this.selectedStatus = 'PENDING';
    this.currentPage = 1;
    this.loadUsers();
  }

  onPageChange(event: any) {
    this.currentPage = event.page + 1;
    this.pageSize = event.rows;
    this.loadUsers();
  }

  // New method to open drawer for user review
  openUserReview(userId: string) {
    this.isDrawerVisible = true;
    this.userReviewLoading = true;
    this.selectedUserForReview = null;

    this.userService.getUserById(userId).subscribe({
      next: (user) => {
        this.selectedUserForReview = user;
        this.userReviewLoading = false;
      },
      error: (error) => {
        console.error('Error loading user details:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load user details'
        });
        this.userReviewLoading = false;
        this.isDrawerVisible = false;
      }
    });
  }

  // Close drawer
  closeUserReview() {
    this.isDrawerVisible = false;
    this.selectedUserForReview = null;
    this.userReviewLoading = false;
    this.approvingUser = false;
    this.denyingUser = false;
  }

  getStatusBadgeSeverity(status: string): 'success' | 'warn' | 'danger' | 'secondary' | 'info' {
    switch (status) {
      case 'INVITED':
        return 'info';
      case 'APPROVED':
        return 'success';
      case 'PENDING':
        return 'warn';
      case 'DENIED':
        return 'danger';
      case 'LOCKED':
        return 'danger';
      case 'DEACTIVATED':
        return 'secondary';
      default:
        return 'info';
    }
  }

  getStatusLabel(status: string): string {
    switch (status) {
      case 'INVITED':
        return 'Invited';
      case 'APPROVED':
        return 'Active';
      case 'PENDING':
        return 'Pending';
      case 'DENIED':
        return 'Denied';
      case 'LOCKED':
        return 'Locked';
      case 'DEACTIVATED':
        return 'Deactivated';
      default:
        return status;
    }
  }

  showActionMenu(event: Event, user: User) {
    this.selectedUser = user;
    this.updateActionMenuItems(user);
    event.stopPropagation();
    if (this.menu) {
      this.menu.show(event);
    }
  }

  editUser() {
    if (this.selectedUser) {
      // Navigate to edit user page with user ID
      this.router.navigate(['/admin/add-edit-user'], {
        queryParams: {
          edit: 'true',
          userId: this.selectedUser._id
        }
      });
    }
  }

  resetPassword() {
    if (this.selectedUser) {
      this.confirmationService.confirm({
        message: `Are you sure you want to reset the password for ${this.selectedUser.firstName} ${this.selectedUser.lastName}?`,
        header: 'Reset Password',
        icon: 'pi pi-exclamation-triangle',
        rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
        accept: () => {
          this.userService.resetUserPassword(this.selectedUser!._id).subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Password reset email sent successfully'
              });
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to reset password'
              });
            }
          });
        }
      });
    }
  }

  generateAndSendNewPassword() {
    if (this.selectedUser) {
      this.confirmationService.confirm({
        message: `Are you sure you want to generate and send a new password to ${this.selectedUser.firstName} ${this.selectedUser.lastName}?`,
        header: 'Send New Password',
        icon: 'pi pi-exclamation-triangle',
        rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
        accept: () => {
          this.userService.generateAndSendNewPassword(this.selectedUser!._id).subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'New password generated and sent successfully'
              });
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to send new password'
              });
            }
          });
        }
      });
    }
  }

  deactivateUser() {
    if (this.selectedUser) {
      this.confirmationService.confirm({
        message: `Are you sure you want to deactivate ${this.selectedUser.firstName} ${this.selectedUser.lastName}?`,
        header: 'Deactivate User',
        icon: 'pi pi-exclamation-triangle',
        rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
        accept: () => {
          this.userService.deactivateUser(this.selectedUser!._id).subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'User deactivated successfully'
              });
              this.loadUsers();
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to deactivate user'
              });
            }
          });
        }
      });
    }
  }

  archiveUser() {
    if (this.selectedUser) {
      this.confirmationService.confirm({
        message: `Are you sure you want to archive ${this.selectedUser.firstName} ${this.selectedUser.lastName}?`,
        header: 'Archive User',
        icon: 'pi pi-exclamation-triangle',
        rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
        accept: () => {
          this.userService.archiveUser(this.selectedUser!._id).subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'User archived successfully'
              });
              this.loadUsers();
              this.router.navigate(['/admin/user-management']);
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to archive user'
              });
            }
          });
        }
      });
    }
  }

  activateUser() {
    if (this.selectedUser) {
      this.confirmationService.confirm({
        message: `Are you sure you want to activate ${this.selectedUser.firstName} ${this.selectedUser.lastName}?`,
        header: 'Activate User',
        icon: 'pi pi-exclamation-triangle',
        rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
        accept: () => {
          this.userService.activateUser(this.selectedUser!._id).subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'User activated successfully'
              });
              this.loadUsers();
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to activate user'
              });
            }
          });
        }
      });
    }
  }

  unarchiveUser() {
    if (this.selectedUser) {
      this.confirmationService.confirm({
        message: `Are you sure you want to unarchive ${this.selectedUser.firstName} ${this.selectedUser.lastName}?`,
        header: 'Unarchive User',
        icon: 'pi pi-exclamation-triangle',
        rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
        accept: () => {
          this.userService.restoreUser(this.selectedUser!._id).subscribe({
            next: () => {
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'User unarchived successfully'
              });
              this.loadUsers();
            },
            error: (error) => {
              this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to unarchive user'
              });
            }
          });
        }
      });
    }
  }

  approveUser(userId: string) {
    this.approvingUser = true;
    this.userService.approveUser(userId).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'User approved successfully'
        });
        // Clear status filter and reset to first page
        this.selectedStatus = null;
        this.currentPage = 1;
        this.loadUsers();
        this.loadPendingApprovals();
        this.closeUserReview();
        this.approvingUser = false;
        this.router.navigate(['/admin/user-management']);
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to approve user'
        });
        this.approvingUser = false;
      }
    });
  }

  denyUser(userId: string) {
    this.confirmationService.confirm({
      message: 'Are you sure you want to deny this user?',
      header: 'Deny User',
      icon: 'pi pi-exclamation-triangle',
      rejectButtonStyleClass: 'p-button-outlined p-button-secondary',
      accept: () => {
        this.denyingUser = true;
        this.userService.denyUser(userId).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'User denied successfully'
            });
            this.loadUsers();
            this.loadPendingApprovals();
            this.closeUserReview();
            this.denyingUser = false;
          },
          error: (error) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to deny user'
            });
            this.denyingUser = false;
          }
        });
      }
    });
  }

  lockUser(userId: string) {
    this.userService.lockUser(userId).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'User locked successfully'
        });
        this.loadUsers();
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to lock user'
        });
      }
    });
  }

  unlockUser(userId: string) {
    this.userService.unlockUser(userId).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'User unlocked successfully'
        });
        this.loadUsers();
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to unlock user'
        });
      }
    });
  }

  getInitials(firstName: string, lastName: string): string {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  exportUsers() {
    // Implement export functionality
    this.messageService.add({
      severity: 'info',
      summary: 'Info',
      detail: 'Export functionality to be implemented'
    });
  }

  addNewUser() {
    this.router.navigate(['/admin/add-edit-user']);
  }

  getFileIcon(fileUrl: string): FileIconInfo {
    return this.fileIconService.getFileIcon(fileUrl);
  }

  downloadIdCard(idCardPath: string) {
    if (!idCardPath) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'No ID card available for download'
      });
      return;
    }

    // Check if it's already a full URL (AWS S3)
    if (idCardPath.startsWith('http')) {
      // It's already a full URL, open in new tab
      window.open(idCardPath, '_blank');
    } else {
      // It's a relative path, construct the full URL
      const fullUrl = `${environment.apiUrl}/files/${idCardPath}`;
      window.open(fullUrl, '_blank');
    }

    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'ID card download started'
    });
  }

  // Get user's products for display
  getUserProducts(): Array<{_id: string, name: string, code: string}> {
    if (!this.selectedUserForReview?.products) return [];
    
    return this.selectedUserForReview.products.map((p: any) => {
      if (typeof p === 'string') {
        // If it's just a string ID, we can't show product details
        return { _id: p, name: 'Unknown Product', code: 'N/A' };
      }
      // If it's a populated object, use the actual data
      return {
        _id: p._id,
        name: p.name || 'Unknown Product',
        code: p.code || 'N/A'
      };
    });
  }

  // Get user's territories grouped by region for display
  getUserTerritoriesGrouped(): {[region: string]: Array<{_id: string, name: string}>} {
    if (!this.selectedUserForReview?.territories) return {};
    
    const grouped: {[region: string]: Array<{_id: string, name: string}>} = {};
    
    this.selectedUserForReview.territories.forEach((t: any) => {
      let territory;
      if (typeof t === 'string') {
        // If it's just a string ID, we can't show region info
        territory = { _id: t, name: 'Unknown Territory', region: 'Unknown Region' };
      } else {
        // If it's a populated object, use the actual data
        territory = {
          _id: t._id,
          name: t.name || 'Unknown Territory',
          region: t.region || 'Unknown Region'
        };
      }
      
      if (!grouped[territory.region]) {
        grouped[territory.region] = [];
      }
      grouped[territory.region].push({
        _id: territory._id,
        name: territory.name
      });
    });
    
    return grouped;
  }

  // Get regions for user territories
  getUserTerritoryRegions(): string[] {
    return Object.keys(this.getUserTerritoriesGrouped());
  }

  // Get territories for a specific region
  getUserTerritoriesForRegion(region: string): Array<{_id: string, name: string}> {
    return this.getUserTerritoriesGrouped()[region] || [];
  }
}
